/**
 * Pré-validação de anexo no CLIENT — lógica PURA, sem React e sem Supabase.
 *
 * Existe por dois motivos concretos, os dois medidos em bytes trafegados:
 *
 *  1. `quota_files_per_day` PURGA o objeto (purgesObject → true). Subir 15 MB
 *     no 4G para descobrir que a cota estourou gasta a franquia do usuário E
 *     apaga o arquivo dele. A cota tem que ser lida ANTES do upload.
 *  2. Tipos que o sniff rejeita de forma DETERMINÍSTICA pela extensão (.xls,
 *     .doc, .gif, .svg…) não precisam de round-trip nenhum. O arquivo aparece
 *     no seletor — some do seletor é pior, vira ícone cinza sem explicação —,
 *     o usuário escolhe, e a mensagem específica sai instantaneamente.
 *
 * O QUE ESTE MÓDULO NÃO É: autoridade. O `executor-ingest` sniffa magic bytes
 * e decide de novo, sem confiar em nada daqui. Um `.xls` renomeado para .xlsx
 * passa por esta função e é pego lá — que é exatamente o comportamento certo:
 * a extensão é sugestão, os bytes são o fato (D3). Toda rejeição aqui usa um
 * código do MESMO vocabulário fechado do servidor, então a copy é a mesma nos
 * dois caminhos e não existe uma segunda tabela de mensagens para divergir.
 *
 * Separado de uploadFile.ts pelo mesmo motivo de executorCaps.ts: aquele
 * importa o client do Supabase (que exige env no import) e este é a parte que
 * precisa de teste.
 */

import type { ExecutorCapsState } from "@/lib/executorCaps";

/**
 * FALLBACK do teto por turno. A política vive em
 * `executor.max_attachments_per_turn` e chega ao client pela RPC de caps
 * (`maxPerTurn`) — este valor só vale enquanto o bootstrap não respondeu, ou
 * contra um servidor anterior ao PR 2. Prendido ao default do servidor por
 * `src/test/executorUpload.test.ts`.
 */
export const MAX_ATTACHMENTS_PER_TURN = 10;

/**
 * Códigos que o client decide sozinho e que TAMBÉM existem no vocabulário do
 * servidor (`_shared/executor/failureReasons.ts`). Listados como literais em
 * vez de importados porque o app não importa código de edge function em
 * runtime; `src/test/executorUpload.test.ts` prende cada um à enumeração real,
 * então inventar um código aqui quebra o build.
 */
export const SHARED_PREFLIGHT_REASONS = [
  "file_empty",
  "file_too_large",
  "quota_files_per_day",
  "format_disabled",
  "xls_legacy_unsupported",
  "unsupported_type_document",
  "unsupported_type_image",
  "unsupported_type_svg",
  "unsupported_type_archive",
  "unsupported_type_executable",
  "unsupported_type_media",
] as const;

/**
 * Códigos que só existem no CLIENT: descrevem coisas que nunca chegam a virar
 * linha em `executor_attachments`, ou que acontecem entre o navegador e o
 * servidor. Não podem colidir com os do servidor — o teste garante — porque a
 * copy é indexada pelo código e um mesmo nome com dois donos vira mensagem
 * errada em produção.
 */
export const CLIENT_ONLY_REASONS = [
  // Composição
  "too_many_attachments",
  "duplicate_attachment",
  // Transporte
  "upload_failed",
  "upload_timeout",
  "register_failed",
  "ingest_unreachable",
] as const;

export type PreflightReason =
  | (typeof SHARED_PREFLIGHT_REASONS)[number]
  | (typeof CLIENT_ONLY_REASONS)[number];

export type PreflightDecision =
  | { ok: true; kind: AttachmentKind | null }
  | { ok: false; reason: PreflightReason };

export type AttachmentKind = "image" | "pdf" | "sheet";

/**
 * Extensão → motivo de rejeição, para os tipos em que a extensão é conclusiva.
 *
 * Critério de entrada nesta tabela: o sniff do servidor rejeita ESTE conteúdo
 * com ESTE código, sempre. Nada aqui é "provavelmente ruim" — .heic ficou de
 * fora de propósito (o servidor pode transcodificar), e arquivo sem extensão
 * também (só os bytes decidem).
 */
const REJECT_BY_EXT: Readonly<Record<string, PreflightReason>> = {
  // Planilha legada BIFF/OLE2: o ExcelJS do ingest lê OOXML, não BIFF.
  xls: "xls_legacy_unsupported",
  // Documentos que não são planilha nem PDF.
  doc: "unsupported_type_document",
  docx: "unsupported_type_document",
  odt: "unsupported_type_document",
  rtf: "unsupported_type_document",
  ppt: "unsupported_type_document",
  pptx: "unsupported_type_document",
  pages: "unsupported_type_document",
  // Imagens fora do conjunto que o modelo lê.
  gif: "unsupported_type_image",
  bmp: "unsupported_type_image",
  tif: "unsupported_type_image",
  tiff: "unsupported_type_image",
  ico: "unsupported_type_image",
  // SVG é rejeitado SEMPRE (D3) — é markup executável, não imagem.
  svg: "unsupported_type_svg",
  // Arquivos compactados.
  zip: "unsupported_type_archive",
  rar: "unsupported_type_archive",
  "7z": "unsupported_type_archive",
  tar: "unsupported_type_archive",
  gz: "unsupported_type_archive",
  // Binários executáveis.
  exe: "unsupported_type_executable",
  dll: "unsupported_type_executable",
  msi: "unsupported_type_executable",
  bat: "unsupported_type_executable",
  cmd: "unsupported_type_executable",
  sh: "unsupported_type_executable",
  so: "unsupported_type_executable",
  dylib: "unsupported_type_executable",
  app: "unsupported_type_executable",
  apk: "unsupported_type_executable",
  dmg: "unsupported_type_executable",
  // Mídia.
  mp3: "unsupported_type_media",
  m4a: "unsupported_type_media",
  wav: "unsupported_type_media",
  ogg: "unsupported_type_media",
  mp4: "unsupported_type_media",
  mov: "unsupported_type_media",
  avi: "unsupported_type_media",
  mkv: "unsupported_type_media",
  webm: "unsupported_type_media",
};

/** Extensões cujo `kind` é previsível — só para checar o kill-switch cedo. */
const KIND_BY_EXT: Readonly<Record<string, AttachmentKind>> = {
  jpg: "image",
  jpeg: "image",
  png: "image",
  webp: "image",
  heic: "image",
  heif: "image",
  pdf: "pdf",
  xlsx: "sheet",
  csv: "sheet",
  tsv: "sheet",
  txt: "sheet",
};

/** Extensão em minúsculas, sem ponto. "" quando o nome não tem extensão. */
export function extensionOf(fileName: string): string {
  const clean = (fileName || "").trim().toLowerCase();
  const dot = clean.lastIndexOf(".");
  if (dot <= 0 || dot === clean.length - 1) return "";
  return clean.slice(dot + 1);
}

/** `kind` inferido pela extensão, ou null quando ela não diz nada. */
export function kindByExtension(fileName: string): AttachmentKind | null {
  return KIND_BY_EXT[extensionOf(fileName)] ?? null;
}

export interface PreflightInput {
  fileName: string;
  byteSize: number;
  /** Anexos já no compositor neste turno (pendentes ou prontos). */
  attachedThisTurn: number;
}

/**
 * Decide se vale a pena gastar banda com este arquivo.
 *
 * A ORDEM dos testes é a mensagem. Tipo antes de tamanho: dizer "arquivo
 * grande demais" para um .mov de 30 MB manda o usuário comprimir um vídeo que
 * nunca seria aceito em tamanho nenhum. Cota por último porque é a única que
 * depende de estado do servidor e a única que o usuário resolve esperando.
 */
export function preflightAttachment(
  input: PreflightInput,
  state: ExecutorCapsState,
): PreflightDecision {
  // Teto vindo do bootstrap — o MESMO número que o servidor aplica.
  if (input.attachedThisTurn >= (state.maxPerTurn || MAX_ATTACHMENTS_PER_TURN)) {
    return { ok: false, reason: "too_many_attachments" };
  }
  if (!Number.isFinite(input.byteSize) || input.byteSize <= 0) {
    return { ok: false, reason: "file_empty" };
  }

  const denied = REJECT_BY_EXT[extensionOf(input.fileName)];
  if (denied) return { ok: false, reason: denied };

  if (input.byteSize > state.caps.maxBytes) {
    return { ok: false, reason: "file_too_large" };
  }

  const kind = kindByExtension(input.fileName);
  if (kind && state.formatsEnabled[kind] === false) {
    return { ok: false, reason: "format_disabled" };
  }

  // usedToday vem do bootstrap (RPC) e é revalidado a cada ingest concluído;
  // o servidor reconta de qualquer forma. `>=` e não `>`: o teto é inclusivo.
  if (state.usedToday >= state.caps.filesPerDay) {
    return { ok: false, reason: "quota_files_per_day" };
  }

  return { ok: true, kind };
}

/**
 * `accept` do <input type="file">.
 *
 * Inclui os tipos que o preflight REJEITA de propósito (.xls, .doc, .gif…):
 * escondê-los do seletor os deixa cinzas e sem explicação, e o usuário fica
 * sem saber se o problema é o arquivo, o app ou o celular. Aparecendo, ele
 * escolhe e recebe "salve como .xlsx" na hora, sem trafegar um byte.
 *
 * NÃO usa `capture`: o atributo abre a câmera direto no mobile e SUPRIME o
 * seletor de arquivos — com ele não existiria anexo de PDF nem de planilha.
 * Sem ele, o seletor nativo do iOS/Android continua oferecendo "Tirar Foto"
 * como uma das origens, então o caminho de câmera não se perde.
 */
export const ATTACH_ACCEPT = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
  "application/pdf",
  ".xlsx",
  ".csv",
  ".tsv",
  ".txt",
  // Aceitos pelo seletor para poderem ser REJEITADOS com mensagem específica.
  ".xls",
  ".doc",
  ".docx",
  ".gif",
  ".svg",
].join(",");

/**
 * O nome que o seletor do sistema devolve NEM SEMPRE é um nome.
 *
 * `File.name` vem do provedor de conteúdo do SO, e para foto escolhida na
 * galeria (Android content provider, biblioteca de Fotos do iOS, Google Fotos)
 * ele costuma ser o identificador opaco do ativo — um UUID, um hash, um
 * `content://…`. Mostrar isso no chip é mostrar ruído: o usuário não reconhece
 * o próprio arquivo, e foi exatamente o que apareceu na primeira rodada em
 * produção.
 *
 * NÃO é o nosso código que perde o nome — `fileName` é escrito uma vez, com
 * `file.name` verbatim, e nada o sobrescreve. Esta função é de APRESENTAÇÃO: o
 * `original_filename` que vai para o banco continua sendo o original, porque é
 * ele que o usuário reconhece se um dia baixar o arquivo de volta.
 */
const OPAQUE_BASENAME = [
  // UUID canônico, com ou sem hífen
  /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i,
  // Hash/blob hexadecimal longo
  /^[0-9a-f]{24,}$/i,
  // iOS/Android costumam sufixar o ativo: "…-1", "…_2"
  /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}[-_]\d+$/i,
  // Só dígitos (timestamp de câmera de alguns Androids)
  /^\d{10,}$/,
] as const;

/** O nome é opaco a ponto de não dizer nada ao usuário? */
export function isOpaqueFileName(fileName: string): boolean {
  const clean = (fileName || "").trim();
  if (!clean) return true;
  const ext = extensionOf(clean);
  const base = ext ? clean.slice(0, clean.length - ext.length - 1) : clean;
  return OPAQUE_BASENAME.some((re) => re.test(base));
}

/**
 * Rótulo do chip. Devolve `null` quando o nome original serve — quem chama
 * decide a copy genérica (ela é i18n e vive no componente, não aqui).
 */
export function displayFileName(fileName: string): string | null {
  if (!isOpaqueFileName(fileName)) return fileName;
  return null;
}

/**
 * Prazo do upload, proporcional ao arquivo.
 *
 * Um valor fixo erra dos dois lados: curto demais mata upload legítimo de
 * 20 MB em 4G ruim; longo demais deixa o usuário olhando "enviando…" por
 * minutos quando a conexão já morreu. 25s de folga + 12s por MB dá ~5min para
 * o teto de 20 MB (~0,55 Mbps sustentado, que é 4G ruim de verdade) e ~37s
 * para 1 MB.
 *
 * Por que um watchdog NOSSO e não do SDK: `FileOptions` do @supabase/storage-js
 * (2.99.3) não tem `signal` nem `timeout`, e `_handleRequest` embrulha o fetch
 * num `new Promise` sem temporizador — um fetch que não assenta devolve uma
 * promise que não assenta. (A plumbing existe no pacote: `download()` e
 * `list()` recebem `FetchParameters { signal }`. Só o `upload()` não expõe.)
 */
export function uploadDeadlineMs(byteSize: number): number {
  const mb = Math.max(byteSize, 0) / (1024 * 1024);
  return Math.round(25_000 + mb * 12_000);
}

/**
 * O CONTRATO do corpo, num lugar só.
 *
 * Os dois consumidores do Executor recebem os anexos pelo mesmo campo —
 * `mirror-chat` no turno de conversa e `deliverable-generate` no toque do chip
 * — e do lado do servidor os dois passam por `sanitizeAttachmentIds`. Ter o
 * nome do campo escrito à mão em cada call site é como um deles vira
 * `attachmentIds` num refactor distraído e o anexo some sem erro nenhum: o
 * servidor não acha a chave, `sanitizeAttachmentIds` devolve [], o turno segue
 * sem o arquivo e ninguém é avisado.
 *
 * OMITE quando vazio, e isso não é estética: turno sem anexo tem de sair com o
 * corpo BYTE A BYTE igual ao de sempre. Um `attachment_ids: []` a mais muda o
 * payload de toda conversa comum do app.
 *
 * Prendido por `src/test/executorBodyContract.test.ts`, que casa a saída daqui
 * com o saneador do servidor.
 */
export function attachmentBodyFields(
  ids: readonly string[],
): { attachment_ids?: string[] } {
  if (!ids || ids.length === 0) return {};
  return { attachment_ids: [...ids] };
}

/**
 * Caminho no bucket: {user_id}/{yyyy}/{mm}/{uuid}.{ext} (D1).
 *
 * A pasta raiz ser o user_id é o que faz as três policies de storage
 * funcionarem — elas comparam `(storage.foldername(name))[1]` com auth.uid().
 * Trocar este formato quebra INSERT, SELECT e DELETE de uma vez só.
 */
export function buildStoragePath(userId: string, fileName: string, now: Date): string {
  const yyyy = String(now.getFullYear());
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const ext = extensionOf(fileName);
  const id = crypto.randomUUID();
  return userId + "/" + yyyy + "/" + mm + "/" + id + (ext ? "." + ext : "");
}
